import { useEffect, useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag as alsBetrag, periodenname } from './api'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import {
  aktuelleSprache,
  beschriftung,
  bewegungsartname,
  gesellschaftsname,
  katalog,
  kontobezeichnung,
  satz as alsSatz,
  t,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { ohneWert } from './Ohnewert'
import { filtertexte } from './Aufriss'
import { belegmarken, belegverweise, vorgangsmarken } from './Belegverweis'
import { betragskopf } from './Anzeigewaehrung'
import type { Kfrfilterregel, Kfrnachweis as Kfrnachweissatz, Kfrnachweiszeile } from './typen'
import { spaltenname } from './Spaltennamen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Der Wertenachweis aus einer Zeile der Kapitalflussrechnung
 * (2.8 Abschnitt 13.14, R267–R270).
 *
 * **Er hat bewusst nicht die Form des Bilanznachweises.** Dort steht
 * Anfangsbestand plus Bewegungen plus Endbestand (2.14 Abschnitt 6.2) — das
 * setzt einen Bestand voraus. Eine KFR-Zeile hat keinen: Sie läuft über
 * mehrere Konten mit verschiedenen Beständen und **ist selbst schon eine
 * Bewegungsrechnung**. Ein Anfangsbestand darüber wäre eine Zahl, die in
 * keiner Zelle steht.
 *
 * Er erklärt sie deshalb nach dem, was sie aufgenommen hat: je Gesellschaft,
 * Konto und Bewegungsart ein Betrag **mit seiner Herkunft**. Und die Herkunft
 * sagt zugleich, ob es dazu einen Beleg geben kann — wo keiner steht, gibt es
 * keinen (R268).
 */

/** Wie eine Wertspalte der Rechnung heißt — dieselben Kennungen wie im Bericht. */

/**
 * Die vier Herkünfte, und was sie über den Beleg sagen.
 *
 * Sie stehen als **Wort** in der Tabelle und nicht als Kennung: „gerechnet"
 * ist die Auskunft, um derentwillen die Unterscheidung überhaupt gemacht wird
 * (R207). Ohne sie sähe eine Bestandsdifferenz aus wie eine Buchung.
 */
const ARTNAME: Record<string, Sprachschluessel> = {
  gebucht: 'kfrnachweis.art.gebucht',
  gerechnet: 'kfrnachweis.art.gerechnet',
  anfangsbestand: 'kfrnachweis.art.anfangsbestand',
}

/**
 * Was vor jeder Zuordnungsregel entschieden ist — die Marken der
 * Filtertabelle (R856). Sie stehen als Wort da, wo sonst die Position steht.
 */
const MARKE: Record<string, Sprachschluessel> = {
  '@beteiligung': 'kfrnachweis.regel.beteiligung',
  '@kreis': 'kfrnachweis.regel.kreis',
  '@kurs': 'kfrnachweis.regel.kurs',
  '@rueckfall': 'kfrnachweis.regel.rueckfall',
  '@fonds': 'kfrnachweis.regel.fonds',
}

/** Die Position einer Regel, wie die Bilanz sie nennt — oder was an ihrer Stelle steht. */
function positionstext(r: Kfrfilterregel): string {
  const marke = MARKE[r.regel]
  if (marke) {
    return t(marke)
  }

  if (r.position) {
    return beschriftung(r.positionNamen) || r.positionCode || r.position
  }

  // Eine Referenz am Konto (R1323) nennt das Konto.
  if (r.quellkonto) {
    return kontobezeichnung(r.quellkonto)
  }

  return t('kfrnachweis.regel.ueberall')
}

/**
 * Der Filter einer Referenz: ihre Bewegungsart, „alle" — oder „alle übrigen"
 * mit dem, was ausgenommen ist (Befund 4: *„jedoch ohne Zugang/Abgang
 * Konsolidierungskreis"*). Dahinter, ob sie das Vorzeichen umkehrt (R1324).
 */
function filtertext(r: Kfrfilterregel): string {
  const umgekehrt = r.umkehren ? ` (${t('kfrnachweis.regel.umgekehrt')})` : ''

  if (MARKE[r.regel]) {
    return r.enthalten.map(bewegungsartname).join(' · ')
  }

  if (r.bewegungsart) {
    return bewegungsartname(r.bewegungsart) + umgekehrt
  }

  if (r.alle) {
    return t('kfrnachweis.regel.alle') + umgekehrt
  }

  const ausser = r.ausgenommen.length > 0
    ? ` — ${t('kfrnachweis.regel.ausser', { arten: r.ausgenommen.map(bewegungsartname).join(', ') })}`
    : ''
  return t('kfrnachweis.regel.alleuebrigen') + ausser + umgekehrt
}

/** Welche Zelle der Kapitalflussrechnung gemeint ist — der vollständige Filter. */
export type Kfrnachweisziel = {
  kreis: string
  /**
   * Der Bezug der **gelieferten** Sicht (R751, R771) — gesetzt heißt: ohne
   * Lauf, gerechnet aus dem Journal. `kreis` steht dann leer.
   */
  bezug?: string
  ledger: string
  /** Die Stichtagsperiode der Rechnung. */
  bis: number
  /**
   * Der Anfang des Zeitraums — `null`, wenn ihn das Raster aufspannt.
   *
   * Im Periodenbild bringt die **Spalte** ihn mit: Ein Klick auf das zweite
   * Quartal soll das zweite Quartal erklären und nicht das Jahr (R269).
   */
  von: number | null
  raster: string
  waehrungsdelta: boolean
  zeile: string
  /** Die Nummer der Zeile im Schema — „Zeile 18" ist die Adresse, unter der ein Prüfer fragt. */
  code: string
  zeilenname: string
  einheit: string | null
  konto: string | null
  spalte: string
  /** Was der Aufriss über Konto und Gesellschaft hinaus festgelegt hat (7.3 Abschnitt 2f). */
  weitere?: Record<string, string | null>
}

export function Kfrwertenachweis({
  ziel,
  aufSchliessen,
  aufBeleg,
}: {
  ziel: Kfrnachweisziel
  aufSchliessen: () => void
  /**
   * In das Buchungsjournal springen — an die Belegzeile, nicht in eine Liste
   * (R209). Derselbe Sprung wie aus dem Bilanznachweis: **ein Ort, zwei Wege**
   * (R156).
   */
  aufBeleg: (sprung: {
    lauf: number | null
    vorgangId: number | null
    belegId: number | null
    konto: string | null
    einheit: string | null
  }) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('kfrwertenachweis:kfrwertenachweis')

  // Die Filtertabelle darüber zieht ihre Breiten für sich (R833): Ihre
  // Spalten tragen andere Dinge als die der Buchungen.
  const { masse: filtermasse, rahmen: filterrahmen } =
    useSpaltenmasse<HTMLTableElement>('kfrwertenachweis:filtertabelle')

  const [satz, setSatz] = useState<Kfrnachweissatz | null>(null)

  // Die Belege dieses Nachweises, nach Kennung greifbar — einmal im Kopf, nicht
  // an jeder Zeile (R433). Dieselbe Form wie im Bilanznachweis.
  const marken = useMemo(() => belegmarken(satz?.belege), [satz])

  // Und die Rechenstufen, nach Code — damit auch hier eine gerechnete Zahl
  // ihren Namen trägt statt des Platzhalterworts (R824).
  const stufen = useMemo(() => vorgangsmarken(satz?.vorgaenge), [satz])
  const sprache = aktuelleSprache()
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(false)
  /** Was dazu am Ort steht — nur ein fachlicher Satz (7.2 Abschnitt 30, R1003/R1004). */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true
    setLaedt(true)
    setFehler(false)

    api
      .kapitalflussNachweis({
        kreis: ziel.kreis,
        ledger: ziel.ledger,
        bis: ziel.bis,
        von: ziel.von,
        raster: ziel.raster,
        waehrungsdelta: ziel.waehrungsdelta,
        zeile: ziel.zeile,
        einheit: ziel.einheit,
        konto: ziel.konto,
        spalte: ziel.spalte,
        weitere: ziel.weitere,
        // **Ein Fenster über zwei Quellen** (R771): Steht ein Bezug, rechnet
        // die Kapitalflußrechnung aus dem Journal statt aus dem Lauf. Was
        // danach kommt — Spalten, Herkünfte, die Probe darunter — ist dasselbe.
        bezug: ziel.bezug,
      })
      .then((s) => gilt && setSatz(s))
      .catch((f) => {
        if (gilt) {
          setFehler(true)
          setFehlersatz(fachlicherSatz(f))
        }
      })
      .finally(() => gilt && setLaedt(false))

    return () => {
      gilt = false
    }
  }, [ziel])

  // Escape schließt, und der Fokus wandert hinein — dieselbe Bedienung wie im
  // Bilanznachweis, seit dem 2026-10-06 vom üblichen Fenster selbst (7.2
  // Abschnitt 41, R1260). Eine Funktion, die nur die Maus kennt, gibt es
  // nicht (R143).

  /**
   * Was in der Spalte *Was* steht: die Bewegungsart, sonst die Herkunft.
   *
   * Eine gebuchte Zeile nennt ihre Bewegungsart — das ist die fachliche
   * Auskunft. Eine gerechnete hat keine, und dann ist die Herkunft selbst die
   * Auskunft.
   */
  function was(z: Kfrnachweiszeile): string {
    return z.bewegungsart ? bewegungsartname(z.bewegungsart) : t(ARTNAME[z.art] ?? 'kfrnachweis.art.gebucht')
  }

  /*
    **Das übliche Fenster, wie der Bilanznachweis** (7.2 Abschnitt 41,
    R1260): verschieben am Kopf, groß und klein im Kopf, die Ecke zieht, Esc
    und Kreuz schließen. Es geht bildschirmfüllend auf wie jedes breite
    Arbeitsfenster (R834) — zwei Tabellen, die untere mit sechs Spalten. Bis
    zum 2026-10-06 stand es eigens gebaut 1180 Punkte breit in der Mitte.
  */
  return (
    /*
      **Es heißt Wertenachweis, wie der Menüpunkt, der es öffnet** (Befund vom
      2026-10-14). Bis dahin stand hier „Wie diese Zahl entstanden ist“; der
      Inhalt ist anders gebaut als der Bilanznachweis, der Name ist derselbe.
    */
    <Dialogfenster
      titel={t('nachweis.titel')}
      weite="breit"
      aufSchliessen={aufSchliessen}
      kopfzusatz={
        /*
          Woher der Nachweis kommt, steht ausgeschrieben da: die Zeile mit
          ihrer Nummer, Konto, Gesellschaft und **die Spalte**. Die Spalte
          ist der Teil, den man leicht vergisst und der den Unterschied
          macht: Ein Klick auf *Kapital* der Abschreibungszeile zeigt die
          Abschreibung der stillen Reserven, ein Klick auf *HB II* die der
          Einzelbilanzen (R205). Im Kopf des Fensters bleibt sie beim Rollen
          stehen.
        */
        [
          `${t('kfr.spalte.zeile')} ${ziel.code} · ${ziel.zeilenname}`,
          ziel.konto === null ? null : kontobezeichnung(ziel.konto),
          ziel.einheit === null ? null : gesellschaftsname(ziel.einheit),
          // Was der Aufriss festgelegt hat — Partner, Kostenstelle —
          // gehört in den Kopf wie Konto und Gesellschaft (R205).
          ...filtertexte(ziel.weitere ?? {}, katalog()),
          spaltenname(ziel.spalte, satz?.gruppe),
        ]
          .filter(Boolean)
          .join(' · ')
      }
    >
      <div className="nachweis">

        {laedt && <p className="hinweis">{t('nachweis.laedt')}</p>}
        {fehlersatz && <p className="hinweis">{fehlersatz}</p>}
        {!laedt && !fehler && satz === null && <p className="hinweis">{t('nachweis.leer')}</p>}

        {satz && (
          <>
            <p className="erlaeuterung">
              {t('kfrnachweis.aufbau', {
                von: periodenname(satz.von),
                bis: periodenname(satz.bis),
              })}
            </p>

            {/*
              **Eine Summenzeile führt nicht ins Journal** (R270). Sie hat
              keine eigenen Beiträge; der Weg dorthin führt über ihre
              Summanden, und die stehen darüber. Der Befund sagt das, statt
              eine leere Tabelle zu zeigen (R145).
            */}
            {satz.befunde.map((b, i) => (
              <p key={i} className="handreichung">
                {alsSatz(b)}
              </p>
            ))}

            {/*
              **Nach welchen Regeln die Zeile ihre Beträge bekommen hat**
              (R856, Befund 4 vom 2026-09-30): *„Es sollte der konkrete Filter
              aufgezeigt werden … eine transparente Tabelle mit den
              entsprechenden Bewegungsarten."* Je Regel die Position, ihre
              Konten, der Filter und der Betrag — aus denselben Zeilen wie die
              Liste darunter, und deshalb mit derselben Summe.
            */}
            {satz.regeln && satz.regeln.length > 0 && (
              <div className="tabellenrahmen">
                <table ref={filterrahmen} className="filtertabelle ziehbaretabelle">
                  <Spaltenmassleiste masse={filtermasse} />
                  <thead>
                    <tr>
                      <th>{t('kfrnachweis.regel.position')}</th>
                      <th>{t('kfrnachweis.regel.konten')}</th>
                      <th>{t('nachweis.spalte.was')}</th>
                      <th className="zahl">{betragskopf(satz.waehrung)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satz.regeln.map((r) => (
                      <tr key={r.regel}>
                        <td>{positionstext(r)}</td>
                        <td>{r.konten.map(kontobezeichnung).join(' · ')}</td>
                        <td>{filtertext(r)}</td>
                        <td className="zahl"><Betrag wert={r.betrag} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {satz.zeilen.length > 0 && (
              <div className="tabellenrahmen">
                <table ref={spaltenrahmen} className="ziehbaretabelle">
                  <Spaltenmassleiste masse={spaltenmasse} />
                  <thead>
                    <tr>
                      <th>{t('nachweis.spalte.periode')}</th>
                      <th>{t('nachweis.spalte.einheit')}</th>
                      <th>{t('nachweis.spalte.konto')}</th>
                      <th>{t('nachweis.spalte.was')}</th>
                      <th className="zahl">{betragskopf(satz.waehrung)}</th>
                      <th>{t('nachweis.spalte.beleg')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satz.zeilen.map((zeile, i) => (
                      <tr key={i}>
                        <td>
                          {/*
                            **Keine Periode heißt: gehört keiner.** Eine
                            Bestandsdifferenz ist der Abstand zweier
                            gelieferter Stände — sie gehört den Rändern des
                            Zeitraums, nicht einem Monat darin (R268).
                          */}
                          {zeile.periode === null ? (
                            <span className="gedaempft">{t('kfrnachweis.zeitraum')}</span>
                          ) : (
                            periodenname(zeile.periode)
                          )}
                        </td>
                        {/* Gesellschaft und Konto haben keinen Vorgabewert:
                            Fehlt die Angabe, steht der Gedankenstrich — wie
                            im Wertenachweis der Bilanz (R1650). */}
                        <td>
                          {zeile.einheit !== null ? gesellschaftsname(zeile.einheit) : (
                            <span className="gedaempft">{ohneWert('gesellschaft')}</span>
                          )}
                        </td>
                        <td>
                          {zeile.konto === null ? (
                            <span className="gedaempft">{ohneWert('konto')}</span>
                          ) : (
                            kontobezeichnung(zeile.konto)
                          )}
                        </td>
                        <td>
                          {was(zeile)}
                          {/*
                            **Gerechnet, nicht gebucht** — und das steht als
                            Wort daneben (R207). Einen Deltaposten gibt es
                            nicht (2.8 Abschnitt 13.32, R1096).
                          */}
                          {zeile.bewegungsart && zeile.art !== 'gebucht' && (
                            <span className="aufrissmarker">{t(ARTNAME[zeile.art]!)}</span>
                          )}
                        </td>
                        <td className={`zahl ${zeile.betrag < 0 ? 'minus' : ''}`}>
                          <Betrag wert={zeile.betrag} />
                        </td>
                        <td>
                          {zeile.belegId !== null || zeile.vorgangId !== null ? (
                            /*
                              **Der Verweis steht im Namen** (R433) — seit dem
                              2026-09-13 auch hier. Bis dahin stand in jeder
                              Zeile derselbe Satz „zum Beleg", während der
                              Bilanznachweis längst den Namen trug: dieselbe
                              Spalte, dieselbe Frage, zwei Antworten.

                              **Und wo keine Lieferung dahintersteht, die
                              Rechenstufe beim Namen** (R824, seit dem
                              2026-09-27) — dieselbe Stelle im Quelltext wie
                              im Bilanznachweis. Dieser Nachweis faßt keine
                              Zeilen zusammen; die Aufzählung ist hier die
                              Liste mit einem Eintrag.
                            */
                            belegverweise(
                              marken,
                              stufen,
                              sprache,
                              [
                                {
                                  laufId: zeile.laufId,
                                  vorgangId: zeile.vorgangId,
                                  vorgangscode: zeile.vorgangscode,
                                  belegId: zeile.belegId,
                                  anteil: zeile.betrag,
                                },
                              ],
                              alsBetrag,
                            ).map((v) => (
                              <button
                                key={v.schluessel}
                                type="button"
                                className="ortsverweis belegverweis"
                                title={v.titel}
                                aria-label={v.beschriftung}
                                onClick={() =>
                                  aufBeleg({
                                    lauf: v.lauf,
                                    vorgangId: v.vorgangId,
                                    belegId: v.belegId,
                                    konto: zeile.konto ?? ziel.konto,
                                    einheit: zeile.einheit ?? ziel.einheit,
                                  })
                                }
                              >
                                {v.aufschrift}
                              </button>
                            ))
                          ) : (
                            /*
                              **Hier gibt es keinen Beleg, und das ist keine
                              Lücke.** Eine Bestandsdifferenz ist keine
                              Buchung; ihr einen
                              Beleg zu geben hieße, eine Buchung zu behaupten,
                              die es nicht gibt (R268).
                            */
                            <span className="gedaempft">{t('kfrnachweis.keinbeleg')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/*
              **Die Probe** (R208): die angeklickte Zelle, die Summe des
              Nachweises und, wenn sie abweichen, die Abweichung. Sie kann
              konstruktionsbedingt nicht auseinanderlaufen — beide Zahlen
              stammen aus demselben Rechengang (R267). Steht hier trotzdem
              etwas, ist es ein Programmfehler und kein Rundungsproblem, und
              deshalb steht es da.
            */}
            {!satz.zeilen.length && satz.befunde.length > 0 ? null : (
              <p className={`probe ${satz.abweichung === 0 ? 'stimmt' : 'weichtab'}`}>
                {satz.abweichung === 0
                  ? t('nachweis.probe.stimmt', {
                      betrag: alsBetrag(satz.zelle),
                      waehrung: satz.waehrung,
                    })
                  : t('nachweis.probe.weichtab', {
                      zelle: alsBetrag(satz.zelle),
                      summe: alsBetrag(satz.summe),
                      abweichung: alsBetrag(satz.abweichung),
                      waehrung: satz.waehrung,
                    })}
              </p>
            )}
          </>
        )}
      </div>
    </Dialogfenster>
  )
}
